import { Link } from "react-router-dom";
import { Download, ArrowLeft, Package, FileImage, FileCode } from "lucide-react";
import SEO from "../components/site/SEO";
import { LogoLockup } from "../components/site/Logo";

const ASSETS = [
  {
    section: "Complete Pack (recommended)",
    items: [
      { file: "QUASECT-brand-pack.zip", label: "Full brand pack (ZIP)", desc: "All 15 files + README. 109 KB.", icon: Package, size: "109 KB", accent: true },
    ],
  },
  {
    section: "Vector (SVG)",
    subtitle: "Scalable, font-independent. Best for print, Illustrator, Figma.",
    items: [
      { file: "QUASECT-lockup-dark.svg",   label: "Full logo — dark backgrounds", desc: "White wordmark on transparent", icon: FileCode },
      { file: "QUASECT-lockup-light.svg",  label: "Full logo — light backgrounds", desc: "Black wordmark on transparent", icon: FileCode },
      { file: "QUASECT-mark.svg",          label: "Icon only — dark theme", desc: "Cyan + neon on transparent", icon: FileCode },
      { file: "QUASECT-mark-light.svg",    label: "Icon only — black", desc: "Solid black on transparent", icon: FileCode },
      { file: "QUASECT-mark-dark-bg.svg",  label: "Icon on dark rounded square", desc: "Social avatar variant", icon: FileCode },
    ],
  },
  {
    section: "Raster (PNG)",
    subtitle: "For email signatures, slides, social media, docs.",
    items: [
      { file: "QUASECT-lockup-transparent-2048.png", label: "Full logo · 2048px transparent", desc: "High-res, dark backgrounds", icon: FileImage },
      { file: "QUASECT-lockup-transparent-1024.png", label: "Full logo · 1024px transparent", desc: "Standard, dark backgrounds", icon: FileImage },
      { file: "QUASECT-lockup-black-1024.png",       label: "Full logo · 1024px on black",     desc: "Solid dark background", icon: FileImage },
      { file: "QUASECT-lockup-white-1024.png",       label: "Full logo · 1024px on white",     desc: "For print / light UIs", icon: FileImage },
      { file: "QUASECT-mark-transparent-1024.png",   label: "Icon only · 1024px transparent",  desc: "Favicon source, watermark", icon: FileImage },
      { file: "QUASECT-mark-transparent-512.png",    label: "Icon only · 512px transparent",   desc: "App icon size",             icon: FileImage },
      { file: "QUASECT-mark-transparent-256.png",    label: "Icon only · 256px transparent",   desc: "Small avatar",              icon: FileImage },
      { file: "QUASECT-mark-dark-1024.png",          label: "Icon · 1024px on dark square",    desc: "LinkedIn / Twitter avatar", icon: FileImage },
    ],
  },
];

export default function BrandKit() {
  return (
    <div data-testid="brand-kit-page" className="min-h-screen bg-[#050507] text-white relative overflow-hidden">
      <SEO title="Quasect Brand Kit — Download Logo & Assets" description="Download Quasect logo files (SVG, PNG) in dark and light variants." path="/brand-kit" />
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />

      <header className="relative max-w-[1200px] mx-auto px-6 md:px-10 pt-6 md:pt-10 flex items-center justify-between">
        <Link to="/" data-testid="brand-back" className="flex items-center gap-2 text-[#8B93A5] hover:text-white transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <Link to="/"><LogoLockup variant="dark" size={28} /></Link>
      </header>

      <main className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3">
          <span className="w-6 h-px bg-[#39FF14]" /> BRAND KIT / DOWNLOADS
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl mt-5 tracking-tighter leading-[1.05] max-w-3xl">
          Quasect logo <span className="text-[#39FF14]">& brand assets.</span>
        </h1>
        <p className="text-[#8B93A5] mt-5 max-w-2xl leading-relaxed">
          Vector (SVG) and raster (PNG) files in dark and light variants — click any button to download instantly.
        </p>

        {ASSETS.map((section) => (
          <section key={section.section} className="mt-14">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{section.section}</h2>
                {section.subtitle && <p className="text-[#8B93A5] mt-1 text-sm">{section.subtitle}</p>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {section.items.map((item) => (
                <a
                  key={item.file}
                  href={`/brand/${item.file}`}
                  download={item.file}
                  data-testid={`download-${item.file}`}
                  className={`group border transition-all p-5 flex flex-col gap-4 relative ${
                    item.accent
                      ? "bg-[#0A0A0D] border-[#39FF14]/40 hover:border-[#39FF14] hover:bg-[#39FF14]/5"
                      : "bg-[#0A0A0D] border-white/10 hover:border-[#00F0FF]/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`w-10 h-10 border flex items-center justify-center ${item.accent ? "border-[#39FF14]/40" : "border-white/15"}`}>
                      <item.icon className={`w-4 h-4 ${item.accent ? "text-[#39FF14]" : "text-[#00F0FF]"}`} />
                    </div>
                    {item.size && (
                      <span className="font-mono text-[10px] tracking-widest uppercase text-[#8B93A5]">{item.size}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-bold text-base md:text-lg leading-tight">{item.label}</div>
                    <div className="font-mono text-[10px] tracking-wider uppercase text-[#8B93A5] mt-1">{item.file}</div>
                    <p className="text-sm text-[#8B93A5] mt-3">{item.desc}</p>
                  </div>
                  <div className={`pt-3 border-t flex items-center justify-between text-xs uppercase tracking-wider ${
                    item.accent ? "border-[#39FF14]/20 text-[#39FF14]" : "border-white/10 text-white group-hover:text-[#39FF14]"
                  }`}>
                    <span>Download</span>
                    <Download className="w-4 h-4" />
                  </div>
                </a>
              ))}
            </div>
          </section>
        ))}

        <section className="mt-16 border border-white/10 bg-[#0A0A0D] p-7 md:p-9">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">USAGE GUIDANCE</div>
          <h3 className="font-display font-bold text-2xl mt-3">Quick reference</h3>
          <div className="grid md:grid-cols-2 gap-6 mt-6 text-sm text-[#8B93A5] leading-relaxed">
            <div>
              <div className="font-mono text-white text-xs tracking-widest uppercase mb-2">Colors</div>
              <ul className="space-y-1 font-mono">
                <li>Neon green — <span className="text-[#39FF14]">#39FF14</span></li>
                <li>Accent cyan — <span className="text-[#00F0FF]">#00F0FF</span></li>
                <li>Dark BG — <span className="text-white">#050507</span></li>
              </ul>
            </div>
            <div>
              <div className="font-mono text-white text-xs tracking-widest uppercase mb-2">Rules</div>
              <ul className="space-y-1">
                <li>Don't stretch, rotate, or recolor the mark.</li>
                <li>Keep clear space equal to the green center dot on all sides.</li>
                <li>Use light variants for print (CMYK-safe).</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
